import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';

// Informe gratis de competencia para la landing (sin registro). Version reducida del Espia de competencia.
const formatPrice = (value, lang, currency) => {
  if (value === null || value === undefined) return '\u2014';
  const amount = Number(value) || 0;
  if (currency) {
    try {
      return new Intl.NumberFormat(lang || 'es', { style: 'currency', currency }).format(amount);
    } catch (err) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  }
  return new Intl.NumberFormat(lang || 'es', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
};

function Stat({ label, value, note }) {
  return (
    <div className="bg-surfaceAlt border border-edge rounded-xl p-4 text-left">
      <p className="text-muted text-xs mb-1">{label}</p>
      <p className="text-white text-lg font-bold break-words">{value}</p>
      {note && <p className="text-muted text-[11px] mt-1">{note}</p>}
    </div>
  );
}

function FreeCompetitorReport() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;
  const [url, setUrl] = useState('');
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const scan = async (e) => {
    e.preventDefault();
    if (!url.trim() || scanning) return;
    setScanning(true);
    setError('');
    setResult(null);
    try {
      const response = await axios.post(`${API_URL}/api/v1/public/competitor-scan`, { url: url.trim() }, { timeout: 45000 });
      setResult(response.data.data);
    } catch (err) {
      const detail = err.response && err.response.data && err.response.data.detail;
      const code = detail && typeof detail === 'object' ? detail.code : null;
      if (code) setError(t(`competitor.errors.${code}`, { defaultValue: t('competitor.errors.unexpected') }));
      else setError(t('competitor.errors.network'));
    } finally {
      setScanning(false);
    }
  };

  const cur = result ? result.currency : null;

  return (
    <div className="max-w-3xl mx-auto px-6 pb-16">
      <div className="bg-surface border border-edge rounded-2xl p-5 sm:p-8">
        <h3 className="text-white text-xl sm:text-2xl font-bold text-center mb-2">{t('landing.freeScanTitle')}</h3>
        <p className="text-muted text-sm text-center mb-6">{t('landing.freeScanSubtitle')}</p>

        <form onSubmit={scan} className="flex flex-col sm:flex-row gap-3 mb-2">
          <input
            type="text"
            inputMode="url"
            autoComplete="off"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={t('competitor.placeholder')}
            className="flex-1 bg-surfaceAlt border border-edge rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent"
          />
          <button
            type="submit"
            disabled={scanning || !url.trim()}
            className={`bg-accent text-panel font-bold text-sm px-5 py-2.5 rounded-lg cursor-pointer ${scanning || !url.trim() ? 'opacity-70' : ''}`}
          >
            {scanning ? t('competitor.scanning') : t('landing.freeScanButton')}
          </button>
        </form>
        <p className="text-muted text-[11px] text-center">{t('landing.freeScanNote')}</p>

        {error && (
          <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-3 mt-5">{error}</div>
        )}

        {result && (
          <div className="mt-6">
            <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 mb-3">
              <p className="text-white text-base font-semibold break-all">{result.store_name || result.store}</p>
              <span className="text-muted text-xs">{result.store}{cur ? ` \u00b7 ${cur}` : ''}</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <Stat label={t('competitor.products')} value={result.total_products} note={result.truncated ? t('competitor.productsTruncated') : null} />
              <Stat label={t('competitor.avgPrice')} value={formatPrice(result.avg_price, lang, cur)}
                    note={`${t('competitor.medianPrice')}: ${formatPrice(result.median_price, lang, cur)}`} />
              <Stat label={t('competitor.range')} value={`${formatPrice(result.min_price, lang, cur)} / ${formatPrice(result.max_price, lang, cur)}`} />
              <Stat label={t('competitor.soldOut')} value={result.availability_known > 0 ? result.sold_out_count : '\u2014'}
                    note={result.availability_known > 0 ? null : t('competitor.stockUnknown')} />
            </div>

            {(result.latest || []).length > 0 && (
              <div className="bg-surfaceAlt border border-edge rounded-xl p-4 mb-4">
                <p className="text-white text-sm font-semibold mb-2">{t('competitor.latestTitle')}</p>
                {result.latest.map((row, i) => (
                  <div key={`${row.url || row.title}-${i}`} className="flex items-center justify-between gap-3 py-1.5 text-sm border-b border-edge last:border-0">
                    <span className="text-white truncate">{row.title}</span>
                    <span className="text-muted whitespace-nowrap">{formatPrice(row.price, lang, cur)}</span>
                  </div>
                ))}
              </div>
            )}
            {!cur && <p className="text-muted text-[11px] mb-4">{t('competitor.currencyUnknown')}</p>}

            <div className="border border-accent rounded-xl p-4 sm:p-5 text-center">
              <p className="text-white text-sm font-semibold mb-1">{t('landing.freeScanCtaTitle')}</p>
              <p className="text-muted text-xs mb-4">{t('landing.freeScanCtaText')}</p>
              <button
                onClick={() => navigate('/register')}
                className="bg-accent text-panel font-bold text-sm px-6 py-2.5 rounded-lg cursor-pointer"
              >
                {t('landing.ctaStart')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FreeCompetitorReport;
